import React, {useEffect, createContext, useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import {useAuth0} from '@auth0/auth0-react';
import {authAPIService, cpAPIService, monolithAPIService} from 'api/services';
import Config from 'hooks/config';
import {getUserData} from 'store/user/user-actions';
import AuthServiceInterceptor from 'api/auth/auth-service-interceptor';
import {configureLoginCookies} from 'utils/auth';

const defaultState = {authAPIReady: false};

export const AuthAPISetupContext = createContext(defaultState);

const AuthAPISetupProvider = ({children}) => {
    const dispatch = useDispatch();
    const {isAuthenticated, getAccessTokenSilently} = useAuth0();
    const [context, setContext] = useState(defaultState);

    const loadInitialUserData = useCallback(async () => {
        await dispatch(getUserData()).unwrap();

        await configureLoginCookies();
    }, [dispatch]);

    // handles setting the auth0 token for each of the api services
    useEffect(() => {
        if (isAuthenticated) {
            AuthServiceInterceptor.setTokenGenerator(getAccessTokenSilently)
                .then(async () => {
                    await AuthServiceInterceptor.attachInterceptorWithToken(
                        monolithAPIService,
                        {audience: Config.auth0V1APIAudience}
                    );
                    // Control Panel services uses a different scope
                    await AuthServiceInterceptor.attachInterceptorWithToken(
                        cpAPIService,
                        {audience: Config.auth0CPServiceAudience}
                    );

                    await AuthServiceInterceptor.attachInterceptorWithToken(
                        authAPIService,
                        {audience: Config.auth0V1APIAudience}
                    );
                })
                .then(async () => {
                    // load the initial user data and update the context so the rest of the app can load
                    await loadInitialUserData();

                    setContext({authAPIReady: true});
                })
                .catch((error) => {
                    setContext({authAPIReady: false});
                    console.error('auth0 Auth0APISetupProvider error', {error});
                });
        }
    }, [isAuthenticated, getAccessTokenSilently, loadInitialUserData]);

    return (
        <AuthAPISetupContext.Provider value={context}>
            {children}
        </AuthAPISetupContext.Provider>
    );
};

AuthAPISetupProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export default AuthAPISetupProvider;
